//region :: Imports ::
import React, {createRef, ReactElement, RefObject} from 'react';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import MForm from '../../Toolkit/Components/Controls/Form';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MComponent from '../../Toolkit/Components/Component';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import MFormSection from '../../Toolkit/Components/Controls/FormSection';
import {MFormOutcome} from '../../Toolkit/Components/Controls/Form';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import withRouter from '../../Toolkit/WithRouter';
import {FieldProps, FormikHelpers} from 'formik';
import * as Yup from 'yup';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import {RequestClient} from '../../Api/RequestClients';
import {AddAdditionalWorkRequest, CreateJobRequest, JobAssignmentClient} from '../../Api/JobAssignmentClient';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import {MNullable, toErrorMessage} from '../../Toolkit/Utils';
import {CreateJobValidator, RequestReply} from '../../Api/ControllerTypes/RequestTypes';
import MAlertDialog from '../../Toolkit/Components/Controls/AlertDialog';
import MFormFieldControl from '../../Toolkit/Components/Controls/FormFieldControl';
import MTextBox from '../../Toolkit/Components/Controls/TextBox';
import {Request} from '../../Api/ControllerTypes/RequestTypes';
import MTextArea from '../../Toolkit/Components/Controls/TextArea';
import MDropDown from '../../Toolkit/Components/Controls/DropDown';
import {MDropDownItem} from '../../Toolkit/Components/Controls/DropDownOption';
import MSaveButton from '../../Toolkit/Components/Controls/SaveButton';
import {MBackIconName, MNavigateToIconName, MSuccessIcon} from '../../Toolkit/Icons';
import MButton from '../../Toolkit/Components/Controls/Button';
import MCalendar from '../../Components/Toolkit/Calendar';
import NSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration'
import MCalendarMulti from '../../Components/Toolkit/CalendarMulti';
import Button from '../../Toolkit/Components/Controls/Button';
import EnumContext from '../../Context/enum-context';

//endregion

/** */
type RouteParams = {
    id: string;
};

type PageRequestData = {
    request: Request;
    noParkingDate: MNullable<Date>;
    workStartTime: MNullable<Date>;
    workEndTime: MNullable<Date>;
};

/** */
enum PageActions {
    None = 0,
    Saving = 1
}

/** */
type Props = WithMsalProps & WithRouterProps<RouteParams> & {}

/** */
type State = {
    pageTitle: string;
    pageSubtitle: string;
    page: MPageProps;
    form: {
        id: string;
        data: PageRequestData;
        validationSchema: any;
    }
    pageActions: PageActions;
    selectedDays: Date[];
}

/** */
class CreateJobPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private m_newRequest: MNullable<Request> | undefined;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_requestClient: NSwagAdapter<RequestClient>;
    private readonly m_jobAssignClient: NSwagAdapter<JobAssignmentClient>;
    private readonly m_alertDialogRef: RefObject<MAlertDialog>;
    private readonly m_daySelectRef: RefObject<MCalendarMulti>;
    
    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_pageRef = createRef<MPage>();
        this.m_requestClient = new NSwagAdapter<RequestClient>(new RequestClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_jobAssignClient = new NSwagAdapter<JobAssignmentClient>(new JobAssignmentClient(
            new NSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_alertDialogRef = createRef<MAlertDialog>();
        this.m_daySelectRef = createRef<MCalendarMulti>();

        this.initializeState();

    }

    /** */
    private bindMethods(): void {

        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.close_Click = this.close_Click.bind(this);
        this.form_onBeforeSubmit = this.form_onBeforeSubmit.bind(this);
        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.form_onAfterSubmit = this.form_onAfterSubmit.bind(this);
        this.form_onError = this.form_onError.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.navigateToRequestButton_onClick = this.navigateToRequestButton_onClick.bind(this);
        this.selectedDaysTemplate = this.selectedDaysTemplate.bind(this);
        this.onStartTimeChange = this.onStartTimeChange.bind(this);
        this.onStartDateChange = this.onStartDateChange.bind(this);
        this.onEndDateChange = this.onEndDateChange.bind(this);
    }

    /** */
    private initializeState(): void {
        //  Page
        const page: MPageProps = {locked: false};

        const data: Request = {
            id: '',
            estimate: '',
            additionalRef: '',
            locked: false,
            requestor: '',
            exchange: '',
            slaType: 0,
            projectValue: 0,
            quoteStatus: 0,
            quoteCompleteDate: null,
            workStream: 3,
            costClaimable: 0,
            quoteValue: 0,
            jobPriority: -1,
            jobType: [],
            jobDetail: '',
            jobStatus: 0,
            workDescription: '',
            estimatedDays: 0,
            address1: '',
            address2: '',
            address3: '',
            city: '',
            county: '',
            postcode: '',
            northing: 0,
            easting: 0,
            gridReference: '',
            dueDate: new Date(),
            slipNumber: '',
            jobStartDate: null,
            endType: 0,
            jobEndDate: null,
            selectedDays: [],
            noParkingRequired: false,
            weekendWorks: false,
            busStop: -1,
            dayOnly: false,
            civilsName: '',
            civilsEmail: '',
            civilsPhone: '',
            siteDocsComplete: false,
            invoiceNumber: '',
            parent: '',
            createdOn: null,
            addRecipients: '',
            permitRef: '',
            permitStatus: -1,
            drawingRef: '',
            resources: [],
            commercialStatus: 0,
            externalInvoicesReceived: 0,
            jobCompletedDate: null,
            commercialCompleteDate: null,
            dfeStatus: 0,
            worksTotalCost: 0,
            uplift: 0,
            dfeValue: 0,
            dfePartialReason: 0,
            dfePartialInfo: '',
            sentToDepotnet: false,
            actualCost: 0,
            drawingsBy: []
        };

        const midnightToday = new Date();
        midnightToday.setHours(0,0,0,0);
        const validationSchema: any = Yup.object().shape({
            request: Yup.object().shape({
                slipNumber: CreateJobValidator.slipNumber,
                jobStartDate: Yup.date()
                                 .min(midnightToday, 'Start date cannot be earlier than today')
                                 .label('jobStartDate')
                                 .required(),
                jobEndDate: CreateJobValidator.jobEndDate,
                selectedDays: CreateJobValidator.selectedDays,
                civilsName: CreateJobValidator.civilsName,
                civilsEmail: CreateJobValidator.civilsEmail,
                civilsPhone: CreateJobValidator.civilsPhone,
                busStop: CreateJobValidator.busStop,
                jobPriority: CreateJobValidator.jobPriority
            }),
            noParkingDate: CreateJobValidator.noParkingDate,
            workStartTime: CreateJobValidator.workStartTime,
            workEndTime: CreateJobValidator.workEndTime
        });
        
        this.state = {
            pageTitle: '',
            pageSubtitle: '',
            page: page,
            form: {
                id: 'createRequestForm',
                data: {request: data, noParkingDate: null, workStartTime: null, workEndTime: null},
                validationSchema: validationSchema,
            },
            pageActions: PageActions.None,
            selectedDays: []
        };

    }

    public componentDidMount(): void {
        //Use the currently logged in user as the default requestor.
        const userName = this.props.msalContext.accounts[0].name;
        this.safeSetState((draft) => {
            draft.form.data.request.requestor = userName!;
        });
    }

    private form_onBeforeSubmit(fieldValues: PageRequestData, formikHelpers: FormikHelpers<PageRequestData>): boolean {
        this.safeSetState(draft => draft.pageActions = PageActions.Saving);
        return false;
    }

    private async form_onSubmit(fieldValues: PageRequestData, formikHelpers: FormikHelpers<PageRequestData>): Promise<void> {
        let selectedWeekDays: Date[] = [];

        if(fieldValues.request.selectedDays === null
            || fieldValues.request.selectedDays === undefined
            || fieldValues.request.selectedDays.length === 0) {

            if(fieldValues.request.endType === 1) {
                selectedWeekDays.push(new Date(fieldValues.request.jobStartDate!));
            } else {
                //No days have been selected so default to weekdays.
                const checkDate = new Date(fieldValues.request.jobStartDate!);
                while(checkDate <= fieldValues.request.jobEndDate!) {
                    if(checkDate.getDay() > 0 && checkDate.getDay() < 6) {
                        selectedWeekDays.push(new Date(checkDate));
                    }
                    checkDate.setDate(checkDate.getDate() + 1);
                }
            }
        } else {
            selectedWeekDays = fieldValues.request.selectedDays;
        }

        const startTime = new Date(fieldValues.request.jobStartDate!);
        startTime.setHours(fieldValues.workStartTime?.getHours() ?? 7);
        startTime.setMinutes(fieldValues.workStartTime?.getMinutes() ?? 0);
        let endTime: MNullable<Date>;
        if(fieldValues.request.endType === 0) {
            endTime = new Date(fieldValues.request.jobEndDate!);
            endTime.setHours(fieldValues.workEndTime?.getHours() ?? 17);
            endTime.setMinutes(fieldValues.workEndTime?.getMinutes() ?? 0);
        } else {
            endTime = null;
        }

        const createRequest = new CreateJobRequest({
            id: fieldValues.request.id,
            slipNumber: fieldValues.request.slipNumber,
            jobStartDate: startTime,
            endType: fieldValues.request.endType!,
            jobEndDate: endTime ?? undefined,
            selectedDays: selectedWeekDays,
            noParkingRequired: fieldValues.request.noParkingRequired,
            weekendWorks: fieldValues.request.weekendWorks,
            busStop: fieldValues.request.busStop,
            dayOnly: fieldValues.request.dayOnly,
            civilsName: fieldValues.request.civilsName,
            civilsEmail: fieldValues.request.civilsEmail,
            civilsPhone: fieldValues.request.civilsPhone,
            jobPriority: fieldValues.request.jobPriority
        });

        const result = await this.m_jobAssignClient.callWithReply(RequestReply, (client => client.raiseJob(createRequest)));

        result.throwIfFailed()

        if(fieldValues.request.noParkingRequired && fieldValues.noParkingDate !== null) {
            //Add new job assignment for installing no parking cones.
            await this.m_jobAssignClient.call(client =>
                (client.addAdditionalWork(new AddAdditionalWorkRequest({
                    requestId: fieldValues.request.id,
                    workItemDate: fieldValues.noParkingDate!,
                    itemNote: 'Install No Parking Cones'
            }))));
        }

        this.m_newRequest = result.reply!.request;
    }

    private form_onAfterSubmit(outcome: MFormOutcome, fieldValues: PageRequestData, formikHelpers: FormikHelpers<PageRequestData>, exception: any | undefined): void {
        if(outcome === 'success') {
            this.m_alertDialogRef.current!.show(
                'Raise Booking Request',
                `A booking has been raised for ${this.m_newRequest!.estimate}.`,
                MSuccessIcon,
                <MButton label="Open request list"
                         color="success"
                         icon={MNavigateToIconName}
                         disabled={false}
                         onClick={this.navigateToRequestButton_onClick}/>);

            return;
        }

        this.safeSetState((draft: State) => {
            draft.pageActions &= ~PageActions.Saving;
            draft.pageActions = PageActions.None;
        });
    }

    private navigateToRequestButton_onClick(): void {
        this.props.history.replace(`/requests`);
    }

    private form_onError(exception: any): boolean
    {
        const handled = false;
        this.m_pageRef.current!.messageBar.error(toErrorMessage(exception));
        return handled;
    }

    private async page_onLoadData(): Promise<void> {
        //Pull request data
        const id = this.props.match.params.id;
        const result = await this.m_requestClient.callWithReply(RequestReply, (client) => client.get(id));
        result.throwIfFailed();

        result.reply!.request!.noParkingRequired = result.reply!.request!.noParkingRequired ?? false;
        result.reply!.request!.weekendWorks = result.reply!.request!.weekendWorks ?? false;
        result.reply!.request!.busStop = result.reply!.request!.busStop ?? false;
        result.reply!.request!.dayOnly = result.reply!.request!.dayOnly ?? false;

        this.safeSetState((draft) => {
            draft.form.data.request = result.reply!.request!;
            draft.selectedDays = result.reply!.request!.selectedDays;
            draft.pageTitle = 'Raise Job';
        });
    }

    /** */
    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MButton label="Back"
                         color="secondary"
                         iconOnly={true}
                         icon={MBackIconName}
                         tooltip="Go Back"
                         disabled={false}
                         onClick={this.close_Click}/>

                <MSpacer/>
                <MSaveButton formId={this.state.form.id}
                             isBusy={this.isSaving}
                             disabled={this.isFormDisabled}/>
            </MToolbar>
        );

    }

    private async close_Click(): Promise<void> {
        this.props.history.goBack();
    }

    private onSelectedDaysChange(fieldProps: FieldProps, selectedDays: Date[] | undefined) {
        let weekendWorks: boolean = false;
        selectedDays?.forEach(d => {
            if(d.getDay() === 0 || d.getDay() === 6) { weekendWorks = true; }
        });

        if(selectedDays !== null){
            selectedDays = selectedDays!.sort((A, B) => A < B ? -1 : A > B ? 1 : 0);
        }

        fieldProps.form.setFieldValue('request.selectedDays', selectedDays);
        fieldProps.form.setFieldValue('request.weekendWorks', weekendWorks);
    }

    private onSelectAllDays(fieldProps: FieldProps): void {
        let selectedDays: Date[] = [];
        let currentDate = new Date(fieldProps.form.values.request.jobStartDate);
        const lastDate = new Date(fieldProps.form.values.request.jobEndDate);

        while(currentDate <= lastDate) {
            selectedDays.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        fieldProps.form.setFieldValue('request.selectedDays', selectedDays);
    }

    private onStartTimeChange(fieldProps: FieldProps, newDate: Date | undefined) {
        let daysSelected = false;
        if((newDate?.getHours() ?? 0) >= 6 && (newDate?.getHours() ?? 0) < 18) {
            daysSelected = true;
        }

        fieldProps.form.setFieldValue('request.dayOnly', daysSelected);
    }

    private onStartDateChange(fieldProps: FieldProps, newDate: Date | undefined) {
        //filter days so any outside the new selection can be removed.
        let searchDate = new Date(newDate!);
        searchDate!.setHours(0, 0, 0, 0);
        const filteredDays = (fieldProps.form.values.request.selectedDays as Date[]).filter(d => d >= newDate!);

        fieldProps.form.setFieldValue('request.jobStartDate', newDate!);
        fieldProps.form.setFieldValue('request.selectedDays', filteredDays.sort((A, B) => A < B ? -1 : A > B ? 1 : 0));
    }

    private onEndDateChange(fieldProps: FieldProps, newDate: Date | undefined) {
        //filter days so any outside the new selection can be removed.
        let searchDate = new Date(newDate!);
        searchDate!.setHours(23, 59, 59, 999);
        const filteredDays = (fieldProps.form.values.request.selectedDays as Date[]).filter(d => d <= newDate!);

        fieldProps.form.setFieldValue('request.jobEndDate', newDate!);
        fieldProps.form.setFieldValue('request.selectedDays', filteredDays.sort((A, B) => A < B ? -1 : A > B ? 1 : 0));
    }

    private selectedDaysTemplate(): string{
        const selectedDays = this.state.form.data.request.selectedDays;
        const dayArray: string[] = [];

        selectedDays.forEach(d => {
            const dayName = d.toLocaleDateString('en-GB', { weekday: 'short' });
            if(!dayArray.includes(dayName)) {
                dayArray.push(dayName);
            }
        });

        return dayArray.join(', ');
    }

    /** */
    public render(): ReactElement {

        const yesNoList: MDropDownItem<boolean>[] = [{label: 'No', value: false},{label: 'Yes', value: true}];
        const shiftPatternList: MDropDownItem<boolean>[] = [{label: 'Nights', value: false},{label: 'Days', value: true}];

        return (
            <MPage title={this.state.pageTitle}
                   subtitle={this.state.pageSubtitle}
                   locked={this.state.page.locked}
                   width={12}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <MAlertDialog ref={this.m_alertDialogRef}/>

                <MForm formId={this.state.form.id}
                       validationSchema={this.state.form.validationSchema}
                       onBeforeSubmit={this.form_onBeforeSubmit}
                       initialValues={this.state.form.data}
                       onSubmit={this.form_onSubmit}
                       onAfterSubmit={this.form_onAfterSubmit}
                       onError={this.form_onError}>

                    <div className="formgrid grid">
                        <div className="col-12">

                            <MFormSection title="Job Data">
                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label="DFE Slip Number"
                                                       fieldName="request.slipNumber">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={Request.SlipNumberMaxLength}
                                                          readonly={false}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}
                                                />
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>
                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label={'Priority'}
                                                       fieldName={'request.jobPriority'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <React.Fragment>
                                                    <MDropDown<number> id={fieldProps.field.name}
                                                                       disabled={this.isFormDisabled}
                                                                       fieldProps={fieldProps}
                                                                       items={this.context?.enums === null ? [] : this.context?.enums.jobPriority.map((enumvalue) => {
                                                                           return {label: enumvalue.name, value: enumvalue.id};
                                                                       })}
                                                                       className={fieldClasses}
                                                                       initialValue={0}
                                                    />
                                                </React.Fragment>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>
                            </MFormSection>

                            <MFormSection>
                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label={'Start Date'} fieldName={'request.jobStartDate'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MCalendar id={fieldProps.field.name}
                                                           fieldProps={fieldProps}
                                                           disabled={this.isFormDisabled}
                                                           className={fieldClasses}
                                                           showTime={false}
                                                           showButtonBar={false}
                                                           hourFormat={'24'}
                                                           onChange={(newDate: Date | undefined) => this.onStartDateChange(fieldProps, newDate)}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-2">
                                    <MFormFieldControl label={'Work Start Time'} fieldName={'workStartTime'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MCalendar id={fieldProps.field.name}
                                                           fieldProps={fieldProps}
                                                           disabled={this.isFormDisabled}
                                                           className={fieldClasses}
                                                           showTime={true}
                                                           timeOnly={true}
                                                           showButtonBar={false}
                                                           hourFormat={'24'}
                                                           onChange={(newDate: Date | undefined) => this.onStartTimeChange(fieldProps, newDate)}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-2">
                                    <MFormFieldControl label={'End Type'}
                                                       fieldName={'request.endType'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <React.Fragment>
                                                    <MDropDown<number> id={fieldProps.field.name}
                                                                       disabled={this.isFormDisabled}
                                                                       fieldProps={fieldProps}
                                                                       items={[{label: 'End Date', value: 0},{label: 'In Situ', value: 1}]}
                                                                       className={fieldClasses}
                                                                       initialValue={0}
                                                    />
                                                </React.Fragment>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label={'End Date'} fieldName={'request.jobEndDate'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MCalendar id={fieldProps.field.name}
                                                           fieldProps={fieldProps}
                                                           disabled={fieldProps.form.values.request.endType !== 0}
                                                           className={fieldClasses}
                                                           showTime={false}
                                                           showButtonBar={false}
                                                           hourFormat={'24'}
                                                           onChange={(newDate) => this.onEndDateChange(fieldProps, newDate)}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-2">
                                    <MFormFieldControl label={'Work End Time'} fieldName={'workEndTime'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MCalendar id={fieldProps.field.name}
                                                           fieldProps={fieldProps}
                                                           disabled={fieldProps.form.values.request.endType !== 0}
                                                           className={fieldClasses}
                                                           showTime={true}
                                                           timeOnly={true}
                                                           showButtonBar={false}
                                                           hourFormat={'24'}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-12">
                                    <MFormFieldControl label={'Selected Days'} fieldName={'request.selectedDays'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <React.Fragment>
                                                    <MCalendarMulti
                                                        ref={this.m_daySelectRef}
                                                        id={fieldProps.field.name}
                                                        fieldProps={fieldProps}
                                                        disabled={this.isFormDisabled}
                                                        className={fieldClasses}
                                                        minDate={fieldProps.form.values.request.jobStartDate ?? undefined}
                                                        maxDate={fieldProps.form.values.request.jobEndDate ?? undefined}
                                                        onChange={(newDates: Date[] | undefined) =>  this.onSelectedDaysChange(fieldProps, newDates)}
                                                    />
                                                    <MSpacer/>
                                                    <Button
                                                        disabled={false}
                                                        label={'All'}
                                                        tooltip={'Select all days in range'}
                                                        onClick={e => this.onSelectAllDays(fieldProps)}
                                                    />
                                                </React.Fragment>
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label={'No Parking Cones'}
                                                       fieldName={'request.noParkingRequired'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MDropDown<boolean> id='noParkingRequired'
                                                                   disabled={true}
                                                                   fieldProps={fieldProps}
                                                                   items={yesNoList}
                                                                   initialValue={false}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label={'Bus Stop Suspension'}
                                                       fieldName={'request.busStop'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MDropDown<number> id='busStop'
                                                                   disabled={true}
                                                                   fieldProps={fieldProps}
                                                                   items={this.context?.enums === null ? [] : this.context?.enums.busStopValues.map((enumvalue) => {
                                                                       return {label: enumvalue.name, value: enumvalue.id};
                                                                   })}
                                                                   initialValue={-1}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label={'Weekend Works'}
                                                       fieldName={'request.weekendWorks'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MDropDown<boolean> id='weekendWorks'
                                                                    disabled={true}
                                                                    fieldProps={fieldProps}
                                                                    items={yesNoList}
                                                                    initialValue={false}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label={'Shift Pattern'}
                                                       fieldName={'request.dayOnly'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MDropDown<boolean> id='dayOnly'
                                                                   disabled={true}
                                                                   fieldProps={fieldProps}
                                                                   items={shiftPatternList}
                                                                   initialValue={false}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label={'No Parking Cone Date'} fieldName={'noParkingDate'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MCalendar id={fieldProps.field.name}
                                                           fieldProps={fieldProps}
                                                           disabled={!this.state.form.data.request.noParkingRequired}
                                                           className={fieldClasses}
                                                           maxDate={fieldProps.form.values.request.jobStartDate ?? undefined}
                                                           showTime={false}
                                                           showButtonBar={false}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>
                            </MFormSection>

                            <MFormSection>
                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Civils Name"
                                                       fieldName="request.civilsName">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={Request.CivilsNameMaxLength}
                                                          readonly={false}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}
                                                />
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Civils Email"
                                                       fieldName="request.civilsEmail">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={Request.CivilsEmailMaxLength}
                                                          readonly={false}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}
                                                />
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Civils Phone"
                                                       fieldName="request.civilsPhone">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={Request.CivilsPhoneMaxLength}
                                                          readonly={false}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isFormDisabled}
                                                          className={fieldClasses}
                                                />
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>
                            </MFormSection>

                            <MFormSection title="General">
                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Estimate"
                                                       fieldName="request.estimate">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={Request.EstimateMaxLength}
                                                          readonly={true}
                                                          fieldProps={fieldProps}
                                                          disabled={true}
                                                          className={fieldClasses}
                                                />
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Requestor"
                                                       fieldName="request.requestor">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={Request.RequestorMaxLength}
                                                          readonly={true}
                                                          fieldProps={fieldProps}
                                                          disabled={true}
                                                          className={fieldClasses}
                                                />
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Exchange"
                                                       fieldName="request.exchange">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={Request.ExchangeMaxLength}
                                                          readonly={true}
                                                          fieldProps={fieldProps}
                                                          disabled={true}
                                                          className={fieldClasses}
                                                />
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label="Project Code"
                                                       fieldName="request.projectValue">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MDropDown<number> id={fieldProps.field.name}
                                                                   disabled={true}
                                                                   fieldProps={fieldProps}
                                                                   items={this.context?.enums === null ? [] : this.context?.enums.projectList.map(project => ({
                                                                       value: project.id,
                                                                       label: project.code + ' - ' + project.name
                                                                   }))}
                                                                   initialValue={-1}
                                                />
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label="Type Of Works"
                                                       fieldName="request.workStream">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MDropDown<number> id={fieldProps.field.name}
                                                                   disabled={true}
                                                                   fieldProps={fieldProps}
                                                                   items={this.context?.enums === null ? [] : this.context?.enums.workStreams.map((enumvalue) => {
                                                                       return {label: enumvalue.name, value: enumvalue.id};
                                                                   })}
                                                                   initialValue={-1}
                                                />
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label="Estimated Days"
                                                       fieldName="request.estimatedDays">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={5}
                                                          readonly={true}
                                                          fieldProps={fieldProps}
                                                          disabled={true}
                                                          className={fieldClasses}
                                                />
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label={'SLA'}
                                                       fieldName={'request.slaType'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MDropDown<number> id="ProjectSelection"
                                                                   disabled={true}
                                                                   fieldProps={fieldProps}
                                                                   items={this.context?.enums === null ? [] : this.context?.enums.slaTypes.map((enumvalue) => {
                                                                       return {label: enumvalue.name, value: enumvalue.id};
                                                                   })}
                                                                   initialValue={-1}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-12">
                                    <MFormFieldControl label="WorkDescription"
                                                       fieldName="request.workDescription">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextArea id={fieldProps.field.name}
                                                          maxLength={Request.WorkDescriptionMaxLength}
                                                          readonly={true}
                                                          fieldProps={fieldProps}
                                                          disabled={true}
                                                          className={fieldClasses}
                                                           rows={5}
                                                />
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>
                            </MFormSection>

                        </div>
                    </div>

                </MForm>

            </MPage>

        );

    }

    private get isFormDisabled(): boolean{
        return this.isSaving;
    }

    private get isSaving() {
        return (this.state.pageActions === PageActions.Saving);
    }
}

export default withMsal(withRouter(CreateJobPage));
